import React, { FormEvent, useEffect, useState } from 'react'
import { FaTicketAlt } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ClipLoader } from 'react-spinners'
import { useDispatch } from 'store'
import { ActionType, Selectors } from 'Reducers'
import { getUserLanguage, mapPrivacyPolicyLink, mapTermsOfUseLink, promoCodeErrorKey } from 'Lib'
import IconInput from 'Components/IconInput'
import Button from 'Components/Button'
import UscHeader from 'Components/UscHeader'
import { theme } from 'styles/theme'
import { PromoCodeValidationError } from 'types/uscTypes'
import {
  ActionBar,
  Card,
  Disclaimer,
  ErrorText,
  Footnote,
  Form,
  FormRow,
  LegalLink,
  Page,
  Progress,
  ProgressFill,
  StepLabel,
  Subtitle,
  Title,
  USC_TOTAL_STEPS
} from 'Containers/UscShared'

const CODE_MIN_LENGTH = 8
const CODE_MAX_LENGTH = 19
const STEP = 1

/** Codes are numeric and vary in length, so only digits are accepted — no grouping. */
const isValidCodeFormat = (code: string): boolean => /^[0-9]{8,19}$/.test(code)

/**
 * Step 1 of the Urban Sports Club voucher flow: the member types the code they got
 * from USC and we check it before asking them to create an account.
 */
const UscRedeemContainer = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const lang = getUserLanguage()
  const [code, setCode] = useState('')
  const [blurred, setBlurred] = useState(false)
  /** Set when the member's input carried more digits than a code can hold. */
  const [tooLong, setTooLong] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  /**
   * A redemption refused after sign-in (already redeemed, code taken meanwhile)
   * lands the member back here. The reason travels through the store because the
   * screen that submitted it is unmounted by then; it is consumed on arrival so a
   * later visit starts clean.
   */
  const redeemErrorKey = useSelector(Selectors.getUscRedeemErrorKey)

  useEffect(() => {
    logEvent('web_usc_redeemScreenViewed')
  }, [])

  useEffect(() => {
    if (!redeemErrorKey) {
      return
    }

    setError(t(redeemErrorKey)!)
    dispatch({ type: ActionType.USC_SET_REDEEM_ERROR, errorKey: null })
  }, [redeemErrorKey, dispatch, t])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Strip anything a member might paste from a voucher email (spaces, dashes)
    const digits = event.target.value.replace(/[^0-9]/g, '')

    setCode(digits.slice(0, CODE_MAX_LENGTH))
    setTooLong(digits.length > CODE_MAX_LENGTH)
    setError('')
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (loading) {
      return
    }

    setLoading(true)
    setError('')

    dispatch({
      type: ActionType.USC_SUBMIT_CODE,
      code,
      resolve: () => setLoading(false),
      reject: (reason: PromoCodeValidationError | Error) => {
        setLoading(false)
        setError(t(promoCodeErrorKey(reason))!)
      }
    })
  }

  const termsOfUseUrl = mapTermsOfUseLink(lang)
  const privacyUrl = mapPrivacyPolicyLink(lang)

  const localError = tooLong
    ? t('usc.errors.codeTooLong', { max: CODE_MAX_LENGTH })!
    : (blurred && code.length > 0 && !isValidCodeFormat(code)
        ? t('usc.errors.codeLength', { min: CODE_MIN_LENGTH, max: CODE_MAX_LENGTH })!
        : '')
  const shownError = error || localError
  const canSubmit = isValidCodeFormat(code) && !tooLong

  return (
    <Page>
      <UscHeader />
      <Card>
        <StepLabel>{t('usc.step', { current: STEP, total: USC_TOTAL_STEPS })}</StepLabel>
        <Progress>
          <ProgressFill step={STEP} />
        </Progress>
        <Title>{t('usc.redeem.title')}</Title>
        <Subtitle>{t('usc.redeem.subtitle')}</Subtitle>
        <Form onSubmit={handleSubmit} aria-busy={loading}>
          <FormRow>
            <IconInput
              icon={<FaTicketAlt />}
              type='text'
              name='code'
              inputMode='numeric'
              autoComplete='off'
              placeholder={t('usc.redeem.codePlaceholder')!}
              value={code}
              onChange={handleChange}
              onBlur={() => setBlurred(true)}
              onFocus={() => setBlurred(false)}
              hasError={!!shownError}
              isInputValid={canSubmit && !error}
            />
            {shownError && <ErrorText role='alert'>{shownError}</ErrorText>}
          </FormRow>

          <ActionBar>
            <Button
              type='submit'
              disabled={!canSubmit}
              margin='0'
            >
              {loading
                ? <ClipLoader size={20} color={theme.colors.basicWhite} speedMultiplier={1} />
                : t('usc.redeem.submit')}
            </Button>
            <Footnote>{t('usc.redeem.footnote')}</Footnote>
            <Disclaimer>
              {t('genderSelection.disclaimerPart1')}
              <LegalLink href={termsOfUseUrl} target='_self'> {t('termsOfUse')} </LegalLink>
              {t('genderSelection.disclaimerPart2')}
              <LegalLink href={privacyUrl} target='_self'>{t('privacyPolicy')}</LegalLink>
              {t('genderSelection.disclaimerPart3')}
            </Disclaimer>
          </ActionBar>
        </Form>
      </Card>
    </Page>
  )
}

export default UscRedeemContainer
